import { useParticlePreview } from '../components/particles/preview-store.js';
import { useConsole } from '../state/console.js';
import { parseMeshEvent, parseMeshLabels } from '../state/mesh-edit.js';
import { useModel } from '../state/model.js';
import { usePaint } from '../state/paint.js';
import { usePlay } from '../state/play.js';
import { useScene } from '../state/scene.js';
import { useSceneView } from '../state/scene-view.js';
import { useSceneSet } from '../state/scene-set.js';
import { useTerrain } from '../components/terrain/terrain-store.js';
import { applyTreeStroke } from '../components/terrain/tree-stroke.js';
import { useUv } from '../state/uv.js';
import { useProcgen } from '../state/procgen.js';
import { remeshStatsFromEngine } from '../components/model/remesh-ops.js';
import { parsePrismEvent, type PrismMode } from './prism.js';

/** Route one engine event (JSON from `host.onEvent`) to the editor stores. */
export function handleEngineEvent(mode: PrismMode, json: string): void {
  const event = parsePrismEvent(json);
  if (!event) return;
  const log = useConsole.getState().push;

  switch (event.type) {
    case 'ready':
      log('debug', `engine:${mode}`, 'view ready');
      // A new scene view: it draws the loaded additive scenes too.
      if (mode === 'scene') queueMicrotask(() => useSceneSet.getState().syncEngine());
      break;
    case 'selection':
      if (mode === 'scene') useScene.getState().selectionFromEngine(event.ids ?? []);
      else if (mode === 'model') useModel.setState({ selected: event.ids?.[0] ?? null });
      break;
    case 'editorView':
      if (mode === 'scene') useSceneView.getState().fromEngine(event as unknown as Record<string, unknown>);
      break;
    case 'sceneChanged':
      if (mode === 'scene') useScene.getState().fromEngine(event.path, event.doc);
      break;
    case 'terrainChanged':
      if (mode === 'scene') useTerrain.getState().fromEngine(event.path, event.text);
      break;
    case 'terrainTrees':
      // Tree painting stroke: one scene edit (terrain tools).
      if (mode === 'scene') applyTreeStroke(event as unknown as Record<string, unknown>);
      break;
    case 'terrainSaved':
      // terrain_save() in the Play view: written through the editor.
      useTerrain.getState().fromPlay(event.path, event.text);
      log('info', `engine:${mode}`, `terrain_save(): wrote ${event.path}`);
      break;
    case 'terrainPlayChanged':
      if (mode === 'play') usePlay.getState().terrainChanged(event.path, event.text, event.edits);
      break;
    case 'modelChanged':
      if (mode === 'model')
        useModel.getState().fromEngine(event.path, event.doc, event.replace === true, typeof event.reload === 'boolean' ? event.reload : undefined);
      break;
    case 'meshEdit':
      if (mode === 'model')
        useModel.getState().meshFromEngine(parseMeshEvent(event as unknown as Record<string, unknown>), event.options);
      break;
    case 'meshLabels':
      if (mode === 'model')
        useModel.getState().meshLabelsFromEngine(parseMeshLabels(event.labels, globalThis.devicePixelRatio || 1));
      break;
    case 'uvLayout':
      if (mode === 'model') useUv.getState().layoutFromEngine(event as unknown as Record<string, unknown>);
      break;
    case 'sculpt':
      if (mode === 'model')
        useModel
          .getState()
          .sculptFromEngine(Boolean(event.active), typeof event.path === 'string' ? event.path : null, event.brush);
      break;
    case 'paint':
      if (mode === 'model') usePaint.getState().fromEngine(event);
      break;
    case 'paintSaved':
      if (mode === 'model') void usePaint.getState().savedFromEngine(event);
      break;
    case 'bake':
      if (mode === 'model') void usePaint.getState().bakeFromEngine(event);
      break;
    case 'modelGizmo':
      if (mode === 'model') useModel.getState().gizmoFromEngine(String(event.mode ?? ''), String(event.space ?? ''));
      break;
    case 'remeshStats':
      if (mode === 'model') remeshStatsFromEngine(event);
      break;
    case 'log':
      log(
        event.level ?? 'info',
        event.source ? (mode === 'play' ? `${event.source} (play)` : event.source) : `engine:${mode}`,
        String(event.message ?? ''),
      );
      if (mode === 'play' && event.level === 'error') usePlay.getState().error(event.message);
      break;
    case 'error':
      log('error', `engine:${mode}`, event.message);
      if (mode === 'play') usePlay.getState().error(event.message);
      break;
    case 'stats':
      if (mode === 'play') usePlay.getState().stats(event.fps, event.drawCalls, event.triangles);
      break;
    case 'playState':
      if (mode === 'play') usePlay.getState().setState(event.state);
      break;
    case 'playSpeed':
      if (mode === 'play') usePlay.setState({ speed: event.speed });
      break;
    case 'playStep':
      if (mode === 'play') usePlay.setState({ frame: event.frame });
      break;
    case 'additiveScenes':
      for (const e of event.errors ?? []) log('warn', `engine:${mode}`, `additive scene: ${e}`);
      break;
    case 'particlePreview':
      useParticlePreview.getState().fromEngine(mode, event as unknown as Record<string, unknown>);
      break;
    case 'procgenStatus':
      if (mode === 'scene') useProcgen.getState().statusFromEngine(event as unknown as Record<string, unknown>);
      break;
    default:
      break;
  }
}
